<template>
  <div
    v-on-click-outside="closeMenu"
    class="abstract-select-multiple"
    role="combobox"
    :aria-expanded="isMenuOpen"
    :aria-controls="'dropdown-' + filter"
    :aria-labelledby="`label-${filter}`"
    :aria-haspopup="true"
  >
    <FilterDropdownButton
      :id="`label-${filter}`"
      :disabled="disabled"
      :is-open="isMenuOpen"
      :label="`${filter} filter dropdown`"
      :count="activeFilters.length"
      @on-click="toggleMenu()"
    >
      <slot name="label" />
      <slot name="badge" />
    </FilterDropdownButton>

    <div
      v-if="isMenuOpen && !disabled"
      class="abstract-select-multiple__dropdown-menu"
    >
      <!-- SearchBar -->
      <SelectSearchBar
        v-if="hasSearch"
        v-model.trim="searchField"
        aria-label="Rechercher un(e) {{ filter }}"
        @keydown.escape="handleEscapeSearchBar"
      />
      <!-- Selected items list -->
      <ul
        v-if="activeFilters.length > 0"
        class="abstract-select-multiple__selected-list"
        role="group"
        aria-label="Filtres sélectionnés"
        @wheel.stop
      >
        <li
          v-for="(selectedItem, index) in activeFilters"
          :key="index"
          class="abstract-select-multiple__selected-item"
          role="option"
        >
          <CheckBox
            checked
            @update:checked="
              (checked) => toggleFilter(getValue(selectedItem), checked)
            "
          >
            {{ getLabel(selectedItem) }}
          </CheckBox>
        </li>
      </ul>

      <SeparatorLine v-if="activeFilters.length > 0" />

      <!-- Option to select -->
      <ul
        role="listbox"
        :aria-label="`${filter} options`"
        class="abstract-select-multiple__option-list"
      >
        <template v-for="(item, index) in nonSelectedItems" :key="index">
          <li
            role="option"
            class="abstract-select-multiple__option-item"
            @click="checkFilter(item)"
          >
            <CheckBox
              :checked="false"
              @update:checked="(checked) => toggleFilter(item, checked)"
            >
              {{ getLabel(item) }}
            </CheckBox>
          </li>
        </template>

        <li
          v-if="searchField && searchedItems.length === 0"
          class="abstract-select-multiple__no-results"
        >
          Aucun résultat ne correspond à cette recherche
        </li>
      </ul>

      <!-- Reset -->
      <div class="abstract-select-multiple__reset">
        <button
          v-focus
          class="btn-secondary abstract-select-multiple__reset__button"
          type="reset"
          aria-label="Réinitialiser le filtre"
          @click="reset"
          @keydown.tab="handleTabKey"
          @keydown.escape="closeMenu"
        >
          <i class="fa-solid fa-arrow-rotate-left"></i>
          <span class="abstract-select-multiple__reset__text">
            Réinitialiser
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { vOnClickOutside } from "@vueuse/components";
import SelectSearchBar from "@follaw-fr/components/reusable/atoms/SelectSearchBar.vue";
import { searchNormalized } from "@follaw-fr/utils/helper.utils";
import CheckBox from "@follaw-fr/components/reusable/atoms/CheckBox.vue";
import FilterDropdownButton from "../atoms/FilterDropdownButton.vue";
import { useToggleMenu } from "@follaw-fr/composables/useToggleMenu";
import SeparatorLine from "@follaw-fr/components/reusable/atoms/SeparatorLine.vue";

const props = defineProps({
  filter: {
    type: String,
    required: true,
  },
  items: {
    type: Array,
    required: true,
    default() {
      return [];
    },
  },
  itemLabel: {
    type: String,
    default: null,
  },
  itemValue: {
    type: String,
    default: null,
  },
  activeFilters: {
    type: Array,
    default() {
      return [];
    },
  },
  hasSearch: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["resetFilter", "addFilterItem", "removeFilterItem"]);

const { isMenuOpen, toggleMenu, closeMenu } = useToggleMenu();

const searchField = ref("");

// Filtered items based on search query
const searchedItems = computed(() => {
  return props.items.filter((item) =>
    searchNormalized(
      props.itemLabel ? item[props.itemLabel] : item,
      searchField.value
    )
  );
});

// Non-selected items from the filtered list
const nonSelectedItems = computed(() => {
  return searchedItems.value.filter((item) => {
    const activeValues = props.itemValue
      ? props.activeFilters.map(getValue)
      : props.activeFilters;
    return !activeValues.includes(getValue(item));
  });
});

const getValue = (item) => (props.itemValue ? item[props.itemValue] : item);

const getLabel = (item) => (props.itemLabel ? item[props.itemLabel] : item);

const toggleFilter = (item, isChecked) => {
  if (isChecked) {
    emit("addFilterItem", { filter: props.filter, item });
  } else {
    emit("removeFilterItem", { filter: props.filter, item });
  }
};

const handleTabKey = (event) => {
  if (!event.shiftKey) {
    event.preventDefault();
    closeMenu();
  }
};

const handleEscapeSearchBar = () => {
  if (!searchField.value.length) {
    closeMenu();
  }
};

const reset = () => {
  searchField.value = "";
  emit("resetFilter", props.filter);
};
</script>

<style lang="scss" scoped>
/* Whole component
========================================================================== */
.abstract-select-multiple {
  position: relative;
  -webkit-user-select: none;
  user-select: none;
}

.abstract-select-multiple__dropdown-menu {
  position: absolute;
  z-index: 10;
  top: 33px;
  left: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-shadow: 0 1px 6px #0000005d;
  padding: 4px;
  background: white;
  border-radius: 13px;
  width: 100%;
  min-width: 270px;
}

.abstract-select-multiple__option-list {
  list-style: none;
  padding: 4px 0;
  margin: 0;
  max-height: 343px;
  overflow-y: auto;
  overscroll-behavior: contain;
  overflow-x: hidden;
}

.abstract-select-multiple__selected-list {
  @extend .abstract-select-multiple__option-list;
  max-height: 120px;
}

.abstract-select-multiple__selected-item,
.abstract-select-multiple__option-item {
  line-height: 1.5;
  text-transform: capitalize;
  padding: 0 11px;
  word-break: break-all;

  &:hover {
    background-color: var(--clr-p1);
    border-radius: 4px;
  }

  :deep(.checkbox-icon) {
    font-size: 1.063em;
  }

  :deep(.checkbox-input:hover + .checkbox-icon) {
    background-color: var(--clr-p1);
  }

  :deep(label) {
    width: 100%;
  }
}

.abstract-select-multiple__no-results {
  padding: 4px 16px;
  margin: 0;
  color: var(--clr-n8);
  opacity: 0.6;
  font-style: italic;
}

.abstract-select-multiple__reset {
  display: flex;
  justify-content: flex-end;
  padding: 0 12px 4px 0;
}

.abstract-select-multiple__reset__button {
  font-size: 0.9rem;
}

.abstract-select-multiple__reset__text {
  margin-left: 4px;
}
</style>
